import { useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { 
  Heart, 
  Lightbulb, 
  ArrowRight, 
  Quote, 
  Linkedin, 
  Twitter, 
  Mail,
  GraduationCap,
  Target,
  Users,
  Award,
  Building2,
  Sparkles,
  Code,
  Server,
  Rocket
} from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { HreflangTags } from "@/components/HreflangTags";

export default function FounderPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Ranvir Gill - Founder & CEO of CheckFirst | Healthcare Innovator</title>
        <meta
          name="description"
          content="Meet Ranvir Gill, founder and CEO of CheckFirst. A healthcare entrepreneur from The Haverford School dedicated to reducing unnecessary ER visits by 39% through AI-powered patient triage technology."
        />
        <meta
          name="keywords"
          content="Ranvir Gill, Ranvir Gill founder, Ranvir Gill CheckFirst, Ranvir Gill CEO, healthcare entrepreneur, AI healthcare founder, digital health innovator, patient triage pioneer, Haverford School"
        />
        <meta name="author" content="Ranvir Gill" />

        <meta property="og:title" content="Ranvir Gill - Founder & CEO of CheckFirst" />
        <meta
          property="og:description"
          content="Healthcare entrepreneur revolutionizing patient triage with AI technology. Learn about Ranvir Gill's mission to transform healthcare access."
        />
        <meta property="og:url" content="https://www.checkfirstcare.com/founder" />
        <meta property="og:type" content="profile" />
        <meta property="og:image" content="https://www.checkfirstcare.com/og-about-founder.jpg" />
        <meta property="profile:first_name" content="Ranvir" />
        <meta property="profile:last_name" content="Gill" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Ranvir Gill - Founder & CEO of CheckFirst" />
        <meta
          name="twitter:description"
          content="Healthcare entrepreneur revolutionizing patient triage with AI technology."
        />
        <meta name="twitter:image" content="https://www.checkfirstcare.com/og-about-founder.jpg" />

        <link rel="canonical" href="https://www.checkfirstcare.com/founder" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ProfilePage",
            "mainEntity": {
              "@type": "Person",
              "name": "Ranvir Gill",
              "image": "https://www.checkfirstcare.com/ranvir-gill.jpg",
              "jobTitle": "Founder & CEO",
              "alumniOf": {
                "@type": "EducationalOrganization",
                "name": "The Haverford School"
              },
              "worksFor": {
                "@type": "Organization",
                "name": "CheckFirst",
                "url": "https://www.checkfirstcare.com"
              },
              "description": "Healthcare entrepreneur and founder of CheckFirst, an AI-powered patient triage platform that has reduced ER visits by 39% across 5 pilot programs serving 4,000+ patients.",
              "knowsAbout": [
                "Healthcare Technology",
                "Artificial Intelligence",
                "Patient Triage Systems",
                "Digital Health Innovation",
                "Healthcare Accessibility",
                "Emergency Room Optimization",
                "Health Policy",
                "Data Analytics"
              ],
              "sameAs": [
                "https://www.linkedin.com/in/ranvir-gill-6894b7199",
                "https://twitter.com/checkfirstcare"
              ]
            }
          })}
        </script>
      </Helmet>
      <HreflangTags />

      <div className="min-h-screen">
        {/* Breadcrumbs */}
        <div className="container mx-auto px-4 lg:px-8 pt-6">
          <Breadcrumbs items={[{ label: 'Founder' }]} />
        </div>

        {/* Hero Section */}
        <section className="relative py-20 lg:py-32 bg-gradient-to-br from-purple-900 via-purple-800 to-teal-600 overflow-hidden">
          <div className="absolute inset-0 bg-black/20"></div>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.1),transparent_50%)]"></div>
          
          <div className="container mx-auto px-4 lg:px-8 relative z-10">
            <div className="max-w-6xl mx-auto">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                {/* Photo */}
                <div className="order-2 lg:order-1 flex justify-center">
                  <div className="relative">
                    <div className="absolute -inset-4 bg-gradient-to-br from-teal-400/30 to-purple-400/30 rounded-3xl blur-2xl"></div>
                    <div className="relative w-72 h-72 lg:w-96 lg:h-96 rounded-3xl overflow-hidden border-4 border-white/20 shadow-2xl">
                      <img 
                        src="/ranvir-gill.jpg" 
                        alt="Ranvir Gill - Founder and CEO of CheckFirst, healthcare entrepreneur and AI healthcare pioneer"
                        className="w-full h-full object-cover"
                        width={384}
                        height={384}
                        loading="eager"
                        decoding="async"
                        fetchPriority="high"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          e.currentTarget.nextElementSibling?.classList.remove('hidden');
                        }}
                      />
                      <div className="hidden w-full h-full bg-gradient-to-br from-purple-600 to-teal-500 flex items-center justify-center">
                        <span className="text-8xl font-bold text-white/80">RG</span>
                      </div>
                    </div>
                    <div className="absolute -bottom-4 -right-4 bg-white rounded-2xl shadow-xl px-6 py-3">
                      <p className="text-sm font-semibold text-purple-900">Founder & CEO</p>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="order-1 lg:order-2 text-center lg:text-left space-y-6">
                  <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2">
                    <Sparkles className="h-4 w-4 text-teal-300" />
                    <span className="text-white/90 text-sm font-medium">Healthcare Innovator</span>
                  </div>
                  
                  <h1 className="text-4xl lg:text-6xl font-bold text-white leading-tight">
                    Ranvir Gill
                  </h1>
                  
                  <p className="text-xl lg:text-2xl text-white/80 leading-relaxed">
                    Transforming Healthcare through Intelligent Triage Technology.
                  </p>

                  {/* Social Links */}
                  <TooltipProvider>
                    <div className="flex gap-4 justify-center lg:justify-start pt-4">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <a 
                            href="https://www.linkedin.com/in/ranvir-gill-6894b7199" 
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
                            aria-label="LinkedIn Profile"
                          >
                            <Linkedin className="h-5 w-5 text-white" />
                          </a>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>View LinkedIn Profile</p>
                        </TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <a 
                            href="https://twitter.com/checkfirstcare" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
                            aria-label="Twitter Profile"
                          >
                            <Twitter className="h-5 w-5 text-white" />
                          </a>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Follow on Twitter</p>
                        </TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <a 
                            href="mailto:ranvgill@haverford.org"
                            className="p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
                            aria-label="Email Ranvir Gill"
                          >
                            <Mail className="h-5 w-5 text-white" />
                          </a>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Send Email</p>
                        </TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <a 
                            href="https://share.google/1A8VfVIkajsRm7x32"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
                            aria-label="Google Profile"
                          >
                            <svg className="h-5 w-5 text-white" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                            </svg>
                          </a>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>View Google Profile</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </TooltipProvider>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Development Journey Section */}
        <section className="py-20 bg-muted/50">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
                  Development Journey
                </h2>
                <p className="text-lg text-muted-foreground">
                  From concept to production—building CheckFirst
                </p>
              </div>

              <div className="relative">
                {/* Timeline line */}
                <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-primary via-teal-500 to-purple-500 hidden sm:block"></div>

                <div className="space-y-8">
                  {/* Phase 1 */}
                  <div className="relative flex gap-6">
                    <div className="hidden sm:flex w-16 h-16 shrink-0 rounded-full bg-gradient-to-br from-primary to-primary/70 items-center justify-center z-10 shadow-lg">
                      <Code className="h-7 w-7 text-white" />
                    </div>
                    <Card className="flex-1 shadow-lg">
                      <CardContent className="p-6">
                        <div className="flex items-center gap-3 mb-3">
                          <span className="px-3 py-1 bg-primary/10 text-primary text-sm font-medium rounded-full">
                            Early 2024
                          </span>
                        </div>
                        <h3 className="text-xl font-semibold text-foreground mb-3">
                          Development Phase
                        </h3>
                        <p className="text-muted-foreground leading-relaxed">
                          CheckFirst development began in early 2024, building the initial platform on a 
                          dedicated development server. During this phase, the focus was on creating the core 
                          AI algorithm and building a robust, HIPAA-compliant infrastructure that could 
                          handle sensitive patient data securely.
                        </p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Phase 2 */}
                  <div className="relative flex gap-6">
                    <div className="hidden sm:flex w-16 h-16 shrink-0 rounded-full bg-gradient-to-br from-teal-500 to-teal-600 items-center justify-center z-10 shadow-lg">
                      <Server className="h-7 w-7 text-white" />
                    </div>
                    <Card className="flex-1 shadow-lg">
                      <CardContent className="p-6">
                        <div className="flex items-center gap-3 mb-3">
                          <span className="px-3 py-1 bg-teal-500/10 text-teal-600 text-sm font-medium rounded-full">
                            Mid 2024
                          </span>
                        </div>
                        <h3 className="text-xl font-semibold text-foreground mb-3">
                          Private Pilot Deployment
                        </h3>
                        <p className="text-muted-foreground leading-relaxed">
                          The application was initially installed exclusively on-site at a Home Care Agency partner. 
                          This private deployment allowed for real-world testing with actual patients and caregivers, 
                          gathering invaluable feedback to refine the AI algorithms and user experience before 
                          broader release.
                        </p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Phase 3 */}
                  <div className="relative flex gap-6">
                    <div className="hidden sm:flex w-16 h-16 shrink-0 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 items-center justify-center z-10 shadow-lg">
                      <Rocket className="h-7 w-7 text-white" />
                    </div>
                    <Card className="flex-1 shadow-lg">
                      <CardContent className="p-6">
                        <div className="flex items-center gap-3 mb-3">
                          <span className="px-3 py-1 bg-purple-500/10 text-purple-600 text-sm font-medium rounded-full">
                            2025
                          </span>
                        </div>
                        <h3 className="text-xl font-semibold text-foreground mb-3">
                          Public Launch
                        </h3>
                        <p className="text-muted-foreground leading-relaxed">
                          In 2025, when Crozer Hospital shut down and healthcare was needed most due to 
                          outrageous wait times, CheckFirst went public—opening the platform to home care 
                          agencies across the tri-state area. The public launch marked the beginning of our 
                          mission to transform healthcare access at scale, now serving 4,000+ patients with 
                          proven results.
                        </p>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Quote Section */}
        <section className="py-16 bg-muted/50">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <Quote className="h-12 w-12 text-primary/30 mx-auto mb-6" />
              <blockquote className="text-2xl lg:text-3xl font-medium text-foreground leading-relaxed italic">
                "Healthcare is not a privilege—it is a human right. Our mission is to ensure everyone can access the right care at the right time."
              </blockquote>
              <cite className="block mt-6 text-muted-foreground font-medium">
                — Ranvir Gill, Founder of CheckFirst
              </cite>
            </div>
          </div>
        </section>

        {/* Story Section */}
        <section className="py-20">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
                  The Journey
                </h2>
                <p className="text-lg text-muted-foreground">
                  From data analysis to healthcare innovation
                </p>
              </div>

              <div className="space-y-8">
                <Card className="shadow-lg">
                  <CardContent className="p-8">
                    <div className="flex gap-6">
                      <div className="hidden sm:flex w-16 h-16 shrink-0 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 items-center justify-center">
                        <GraduationCap className="h-8 w-8 text-primary" />
                      </div>
                      <div className="space-y-4">
                        <h3 className="text-xl font-semibold text-foreground">The Background</h3>
                        <div className="text-muted-foreground leading-relaxed space-y-4">
                          <p>
                            As a student at The Haverford School in Pennsylvania, I developed a passion 
                            for using data to solve real-world problems. My experience as a Quality Data-Analyst at 
                            Care First Home Health gave me firsthand insight into the healthcare system's challenges.
                          </p>
                          <p>
                            Analyzing clinical data for 4,000+ patients, I discovered patterns linking quality indicators 
                            with company revenue—particularly the devastating impact of preventable ER visits on both 
                            patients and healthcare organizations.
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="shadow-lg">
                  <CardContent className="p-8">
                    <div className="flex gap-6">
                      <div className="hidden sm:flex w-16 h-16 shrink-0 rounded-2xl bg-gradient-to-br from-teal-500/20 to-teal-500/5 items-center justify-center">
                        <Lightbulb className="h-8 w-8 text-teal-600" />
                      </div>
                      <div className="space-y-4">
                        <h3 className="text-xl font-semibold text-foreground">The Spark</h3>
                        <div className="text-muted-foreground leading-relaxed space-y-4">
                          <p>
                            I realized that patients, regardless of severity, tend to default towards emergency departments 
                            because they lack trusted guidance in moments of medical uncertainty. This behavioral trend 
                            results in persistent ER overcrowding, longer wait times, and delayed treatment for high-acuity patients.
                          </p>
                          <p>
                            The solution became clear: build a patient-facing ML triage framework that evaluates symptoms 
                            for key indicators—severity, duration, progression, and red-flag symptoms—to help patients 
                            decide where to go: the ER, a primary care provider, or urgent care.
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="shadow-lg">
                  <CardContent className="p-8">
                    <div className="flex gap-6">
                      <div className="hidden sm:flex w-16 h-16 shrink-0 rounded-2xl bg-gradient-to-br from-purple-500/20 to-purple-500/5 items-center justify-center">
                        <Target className="h-8 w-8 text-purple-600" />
                      </div>
                      <div className="space-y-4">
                        <h3 className="text-xl font-semibold text-foreground">The Mission</h3>
                        <div className="text-muted-foreground leading-relaxed space-y-4">
                          <p>
                            CheckFirst operates as a B2B healthcare infrastructure platform in partnership with home care 
                            agencies affected by inappropriate emergency department utilization. Agencies license CheckFirst 
                            as a SaaS tool to support patient triage and care navigation outside the hospital.
                          </p>
                          <p>
                            Through pilot programs serving 4,000+ patients in the tri-state area, we have achieved a 39% reduction in 
                            non-critical emergency department visits—proving that intelligent triage technology can 
                            transform healthcare outcomes while reducing costs for everyone.
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="shadow-lg">
                  <CardContent className="p-8">
                    <div className="flex gap-6">
                      <div className="hidden sm:flex w-16 h-16 shrink-0 rounded-2xl bg-gradient-to-br from-orange-500/20 to-orange-500/5 items-center justify-center">
                        <Heart className="h-8 w-8 text-orange-600" />
                      </div>
                      <div className="space-y-4">
                        <h3 className="text-xl font-semibold text-foreground">The Vision</h3>
                        <div className="text-muted-foreground leading-relaxed space-y-4">
                          <p>
                            At CheckFirst, we believe that everyone deserves care that truly fits their needs. 
                            Our vision is to make healthcare more accessible, fair, and compassionate by helping people 
                            choose the right care at the right time.
                          </p>
                          <p>
                            By preventing unnecessary hospital visits, we are working to shorten wait times, ease the 
                            burden on emergency rooms, and ensure that every community can get help when it matters most.
                            Because healthcare is not a privilege—it is a human right.
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Impact Stats */}
        <section className="py-20 bg-gradient-to-br from-purple-900 via-purple-800 to-teal-600">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
                  Proven Impact
                </h2>
                <p className="text-lg text-white/70">
                  Real results from our pilot programs with home care agencies
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="text-center p-6 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/10">
                  <div className="text-4xl font-bold text-white mb-2">4,000+</div>
                  <div className="text-white/70 text-sm">patients in the tri-state area</div>
                </div>
                <div className="text-center p-6 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/10">
                  <div className="text-4xl font-bold text-white mb-2">10,776</div>
                  <div className="text-white/70 text-sm">billable hours saved</div>
                </div>
                <div className="text-center p-6 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/10">
                  <div className="text-4xl font-bold text-white mb-2">$232K</div>
                  <div className="text-white/70 text-sm">estimated revenue saved</div>
                </div>
                <div className="text-center p-6 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/10">
                  <div className="text-4xl font-bold text-white mb-2">39%</div>
                  <div className="text-white/70 text-sm">reduction in non-critical ER visits</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="py-20">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
                  Core Values
                </h2>
                <p className="text-lg text-muted-foreground">
                  The principles that guide CheckFirst
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                <Card className="shadow-card hover:shadow-card-hover transition-smooth">
                  <CardContent className="p-6 text-center space-y-4">
                    <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                      <Heart className="h-7 w-7 text-primary" />
                    </div>
                    <h3 className="font-semibold text-foreground">Patient First</h3>
                    <p className="text-sm text-muted-foreground">
                      Every decision starts with patient well-being
                    </p>
                  </CardContent>
                </Card>

                <Card className="shadow-card hover:shadow-card-hover transition-smooth">
                  <CardContent className="p-6 text-center space-y-4">
                    <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-teal-500/20 to-teal-500/5 flex items-center justify-center">
                      <Users className="h-7 w-7 text-teal-600" />
                    </div>
                    <h3 className="font-semibold text-foreground">Accessibility</h3>
                    <p className="text-sm text-muted-foreground">
                      Healthcare guidance for everyone, everywhere
                    </p>
                  </CardContent>
                </Card>

                <Card className="shadow-card hover:shadow-card-hover transition-smooth">
                  <CardContent className="p-6 text-center space-y-4">
                    <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-purple-500/20 to-purple-500/5 flex items-center justify-center">
                      <Award className="h-7 w-7 text-purple-600" />
                    </div>
                    <h3 className="font-semibold text-foreground">Excellence</h3>
                    <p className="text-sm text-muted-foreground">
                      Clinical accuracy and continuous improvement
                    </p>
                  </CardContent>
                </Card>

                <Card className="shadow-card hover:shadow-card-hover transition-smooth">
                  <CardContent className="p-6 text-center space-y-4">
                    <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-orange-500/20 to-orange-500/5 flex items-center justify-center">
                      <Building2 className="h-7 w-7 text-orange-600" />
                    </div>
                    <h3 className="font-semibold text-foreground">Partnership</h3>
                    <p className="text-sm text-muted-foreground">
                      Working together with healthcare providers
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-muted/50">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-3xl mx-auto text-center space-y-8">
              <h2 className="text-3xl lg:text-4xl font-bold text-foreground">
                Join Us in Transforming Healthcare
              </h2>
              <p className="text-lg text-muted-foreground">
                Whether you are a patient seeking guidance or a healthcare organization looking to improve 
                outcomes, we are here to help.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" asChild>
                  <Link to="/triage">
                    Try CheckFirst
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button size="lg" variant="outline" asChild>
                  <Link to="/contact">
                    Get in Touch
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
